/**
 * CurrencyItem Component
 *
 * Represents a single entry inside the currency dropdown list used in Expand Sum.
 * Each item displays the currency icon, symbol, code, and translated long name.
 * Clicking a currency triggers stat recalculation and updates the footer to reflect
 * the newly selected currency.
 *
 * Props:
 *  - `currencyItem` (string): The currency code represented by this item
 *        (e.g., "USD", "EUR", "GBP").
 *
 *  - `isFirstItem` (boolean, optional):
 *        When true, this item behaves as a "Reset" or "Back" option. Clicking it:
 *          • Resets table footer state (via `resetTableFooter`)
 *          • Does NOT fetch currency-specific data
 *
 *  - `currencyMenuHandler` (function, optional):
 *        Called immediately after selecting a non-first currency item.
 *        Typically used to collapse the currency menu dropdown.
 *
 * Behavior:
 *
 * 1. **First Item (Reset Entry)**
 *    - If `isFirstItem === true`, clicking this item:
 *         → dispatch(resetTableFooter())
 *         → stops execution (no fetch, no currency select)
 *    - Often used as a “Default currency” or “Back to summary” entry.
 *
 * 2. **Regular Currency Item**
 *    - Clicking triggers:
 *        • setShouldExpandSum(true)
 *        • setSelectedCurrency(currencyItem)
 *        • optional menu close (`currencyMenuHandler`)
 *        • clearCurrencyData()
 *        • fetchStats() with:
 *            type: EXPAND_SUM
 *            request: including updated CCY
 *
 * 3. **Currency Rendering**
 *    - Retrieves:
 *         • `imgUrl`: currency flag/icon
 *         • `symbol`: symbol or special glyph for display
 *
 * 4. **i18n**
 *    - Long-form currency name displayed using:
 *        t(`currency.${currencyItem.toLowerCase()}`)
 *
 * Example:
 *   <CurrencyItem
 *      currencyItem="USD"
 *      isFirstItem={false}
 *      currencyMenuHandler={() => setOpen(false)}
 *   />
 */

import React from 'react';

import { useAppDispatch } from '../../../../../../hook/useAppDispatch';
import {
  clearCurrencyData,
  fetchStats,
  resetTableFooter,
  setSelectedCurrency,
  setShouldExpandSum,
} from '../../../../../../store/slice/stats-slice';
import { StatsFetchType } from '../../../../../../enum/StatsFetchType';
import { mapCCYAssets } from '../../../../../../util/currency';
import { useTranslation } from 'react-i18next';
import { useStatsPayload } from '../../../../../../hook/useStatsPayload';

interface Props {
  currencyItem: string;
  isFirstItem?: boolean;
  currencyMenuHandler?: () => void;
}

const CurrencyItem: React.FC<Props> = ({ currencyItem, isFirstItem, currencyMenuHandler }) => {
  const dispatch = useAppDispatch();
  const statsPayload = useStatsPayload();

  const { imgUrl, symbol } = mapCCYAssets(currencyItem);
  const { t } = useTranslation();

  const dispatchViewByCurrencyItem = async () => {
    dispatch(clearCurrencyData());
    await dispatch(
      fetchStats({
        type: StatsFetchType.EXPAND_SUM,
        request: { ...statsPayload, groupBy: 8, CCY: currencyItem },
      }),
    );
  };

  return (
    <li
      className="currency_item"
      onClick={() => {
        if (isFirstItem) {
          dispatch(resetTableFooter());
          return;
        }

        dispatch(setShouldExpandSum(true));
        dispatch(setSelectedCurrency(currencyItem));

        if (currencyMenuHandler) currencyMenuHandler();

        dispatchViewByCurrencyItem();
      }}
    >
      <span className="ccy_details">
        <img src={imgUrl} alt={currencyItem} />

        <span>
          <p className="ccy_item">{currencyItem}</p>
          <p>{t(`currency.${currencyItem.toLowerCase()}`)}</p>
        </span>
      </span>

      <span className="ccy_symbol">{symbol}</span>
    </li>
  );
};

export default CurrencyItem;
